import {
  intranetFilePreviewPath,
  intranetFilePreviewSchema,
  type IntranetFile,
} from '@gb-ticket/shared-types';
import { DownloadIcon, Loader2Icon, Trash2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { apiFetch } from '@/lib/api';
import { formatBytes, formatDateTime } from '@/lib/format';
import { fileDownloadHref, fileTypeLabel } from '@/products/intranet/features/intranet/file-type';

const WORD_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

/** Download button for the current version (or a hint while it is being scanned). */
export function CurrentFileDownload({
  file,
  onDelete,
  deletePending = false,
}: {
  file: IntranetFile | undefined;
  onDelete?: (file: IntranetFile) => void;
  deletePending?: boolean;
}) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [wordText, setWordText] = useState<string | null>(null);
  const [wordError, setWordError] = useState<string | null>(null);
  const [wordPending, setWordPending] = useState(false);
  const isWord = file?.mimeType === WORD_MIME;
  useEffect(() => {
    if (!previewOpen || !isWord || !file) {
      return;
    }
    let cancelled = false;
    void apiFetch(intranetFilePreviewPath(file.id), intranetFilePreviewSchema)
      .then((body) => {
        if (!cancelled) {
          setWordText(body.text);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setWordError('Die Vorschau konnte nicht geladen werden.');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setWordPending(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [previewOpen, isWord, file]);
  if (!file) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-muted/30 px-4 py-6 text-sm text-muted-foreground">
        Noch keine Datei hinterlegt.
      </div>
    );
  }
  if (file.scanStatus === 'PENDING') {
    return (
      <div className="flex items-center gap-2 rounded-xl border border-border bg-card px-4 py-4 text-sm text-muted-foreground shadow-sm">
        <Loader2Icon className="size-4 animate-spin" aria-hidden />
        Die Datei wird geprüft und ist gleich verfügbar.
      </div>
    );
  }
  const isPdf = file.mimeType === 'application/pdf';
  const previewable = (isPdf || isWord) && file.downloadable !== false;
  return (
    <div className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 space-y-1">
          <p className="truncate font-semibold text-primary">{file.filename}</p>
          <p className="text-sm text-muted-foreground">
            {fileTypeLabel(file)} · {formatBytes(file.sizeBytes)} · Version {file.version}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {previewable ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setWordText(null);
                setWordError(null);
                setWordPending(true);
                setPreviewOpen((current) => !current);
              }}
            >
              {previewOpen ? 'Vorschau schließen' : 'Vorschau'}
            </Button>
          ) : null}
          {file.downloadable === false ? null : (
            <Button asChild>
              <a href={fileDownloadHref(file.id)} download>
                <DownloadIcon aria-hidden />
                Herunterladen
              </a>
            </Button>
          )}
          {onDelete ? (
            <Button
              type="button"
              variant="outline"
              disabled={deletePending}
              onClick={() => onDelete(file)}
            >
              <Trash2Icon aria-hidden />
              Löschen
            </Button>
          ) : null}
        </div>
      </div>
      {previewOpen && isPdf ? (
        <iframe
          title={`Vorschau ${file.filename}`}
          src={`${fileDownloadHref(file.id)}?preview=true`}
          className="h-[70vh] w-full rounded-lg border border-border"
        />
      ) : null}
      {previewOpen && isWord ? (
        <div className="max-h-[70vh] overflow-auto rounded-lg border border-border p-4">
          {wordPending ? (
            <p className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2Icon className="size-4 animate-spin" aria-hidden />
              Vorschau wird geladen …
            </p>
          ) : wordError ? (
            <p role="alert" className="text-sm text-destructive">
              {wordError}
            </p>
          ) : (
            <p className="text-sm whitespace-pre-wrap">{wordText}</p>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function FileVersions({
  files,
  onDelete,
  deletePending = false,
}: {
  files: readonly IntranetFile[];
  onDelete?: (file: IntranetFile) => void;
  deletePending?: boolean;
}) {
  if (files.length === 0) {
    return null;
  }
  return (
    <section
      aria-labelledby="versionen"
      className="space-y-3 rounded-xl border border-border bg-card p-4 shadow-sm sm:p-5"
    >
      <h2 id="versionen" className="text-lg font-semibold text-primary">
        Versionen
      </h2>
      <ul className="divide-y rounded-lg border">
        {files.map((file) => (
          <li
            key={file.id}
            className="flex min-h-11 flex-col gap-2 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between"
          >
            <span className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="font-medium">Version {file.version}</span>
              {file.isCurrent ? <Badge>aktuell</Badge> : null}
              <span className="text-muted-foreground">
                {formatDateTime(file.uploadedAt)} · {file.uploadedBy.displayName} ·{' '}
                {formatBytes(file.sizeBytes)}
              </span>
            </span>
            <span className="flex flex-wrap items-center gap-3">
              {file.scanStatus === 'PENDING' ? (
                <span className="text-muted-foreground">wird geprüft …</span>
              ) : file.downloadable === false ? (
                <span>{file.filename}</span>
              ) : (
                <a
                  className="font-medium text-primary underline-offset-4 hover:underline"
                  href={fileDownloadHref(file.id)}
                  download
                >
                  Herunterladen
                </a>
              )}
              {onDelete ? (
                <button
                  type="button"
                  disabled={deletePending}
                  className="inline-flex min-h-9 items-center gap-1 text-sm font-medium text-destructive underline-offset-4 hover:underline focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:outline-none disabled:opacity-60"
                  onClick={() => onDelete(file)}
                >
                  <Trash2Icon className="size-3.5" aria-hidden />
                  Löschen
                </button>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
